import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import { ConversationList } from './conversation-list'
import { MessageThread } from './message-thread'
import { ReviewQueue } from './review-queue'
import { SectionHelp } from '@/components/help/section-help'
import { type Conversation, type InboxFilter, type InboxScope } from './use-inbox'

export function InboxPage() {
  const [filter, setFilter] = useState<InboxFilter>('all')
  const [scope, setScope] = useState<InboxScope>('all')
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [mobileView, setMobileView] = useState<'list' | 'thread'>('list')

  // Notification deep link (/inbox?lead=<id>, 2026-09-18). Every reply /
  // escalation notification already carried the lead id, but the page
  // ignored it and landed on an empty inbox.
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkLeadId = searchParams.get('lead')
  const apiFetch = useApi()
  const { data: deepLead } = useQuery({
    queryKey: ['inbox-deep-link-lead', deepLinkLeadId],
    enabled: Boolean(deepLinkLeadId) && !deepLinkLeadId?.startsWith('chat:'),
    queryFn: async () => {
      const res = await apiFetch<{ id: string; full_name?: string | null; first_name?: string | null; last_name?: string | null; company?: string | null; avatar_url?: string | null; manually_handled_at?: string | null }>(
        `/api/leads/${encodeURIComponent(deepLinkLeadId as string)}`,
      )
      return res.success ? (res.data ?? null) : null
    },
    staleTime: 60_000,
  })
  // Open the thread immediately from the lead record; the list swaps in the
  // full conversation row once it finds it.
  useEffect(() => {
    if (!deepLinkLeadId) return
    if (selectedConversation?.lead_id === deepLinkLeadId) return
    const name =
      deepLead?.full_name ||
      [deepLead?.first_name, deepLead?.last_name].filter(Boolean).join(' ') ||
      'Conversation'
    setSelectedConversation({
      id: deepLinkLeadId,
      lead_id: deepLinkLeadId,
      lead_name: name,
      lead_company: deepLead?.company ?? '',
      lead_avatar: deepLead?.avatar_url ?? undefined,
      status: 'active',
      last_message: '',
      last_message_at: new Date().toISOString(),
      unread: false,
      manually_handled_at: deepLead?.manually_handled_at ?? null,
    })
    setMobileView('thread')
  }, [deepLinkLeadId, deepLead, selectedConversation?.lead_id])

  function handleSelectConversation(conversation: Conversation) {
    setSelectedConversation(conversation)
    setMobileView('thread')
    // Picking another conversation ends the deep link.
    if (deepLinkLeadId && conversation.lead_id !== deepLinkLeadId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('lead')
        return next
      }, { replace: true })
    }
  }

  function handleBackToList() {
    setMobileView('list')
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <h1 className="text-sm font-semibold text-[var(--color-silver-bright)]">Inbox</h1>
        <SectionHelp sectionKey="inbox" />
      </div>
      {/* Review queue banner — always at top */}
      <div data-tour="inbox-review">
        <ReviewQueue />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop: always show both panels */}
        {/* Mobile: show only the active view */}

        {/* Conversation list */}
        <div
          className={cn(
            'h-full',
            // Mobile: full width when showing list, hidden when showing thread
            mobileView === 'list' ? 'flex' : 'hidden',
            // Desktop: always visible, fixed width
            'md:flex',
          )}
          data-tour="inbox-list"
        >
          <ConversationList
            deepLinkLeadId={deepLinkLeadId}
            filter={filter}
            onFilterChange={setFilter}
            scope={scope}
            onScopeChange={setScope}
            selectedLeadId={selectedConversation?.lead_id ?? null}
            onSelect={handleSelectConversation}
          />
        </div>

        {/* Thread panel */}
        <div
          className={cn(
            'flex flex-1 flex-col overflow-hidden',
            // Mobile: only visible when thread view active
            mobileView === 'thread' ? 'flex' : 'hidden',
            'md:flex',
          )}
          data-tour="inbox-thread"
        >
          {/* Mobile back button */}
          {selectedConversation && (
            <div className="border-b px-3 py-2 md:hidden">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={handleBackToList}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to conversations
              </Button>
            </div>
          )}

          <MessageThread
            conversation={selectedConversation}
            className="flex-1"
          />
        </div>
      </div>
    </div>
  )
}
